import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  ActionButtonsRow,
  Result,
  ReferenceCard,
  MathDiv,
  Select,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'
import { Table, Th, Td, Tr, TableContainer } from './styles'

export function ConcreteBendingTension() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(25)
  const [fyk, setFyk] = useState(500)
  const [b, setB] = useState('')
  const [h, setH] = useState('')
  const [dlb, setDlb] = useState('')
  const [dlt, setDlt] = useState('')
  const [mk, setMk] = useState('')
  const [nk, setNk] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [rhoMin, setRhoMin] = useState(0.15)
  const [distribution, setDistribution] = useState('beam')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-bending-tension-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory))
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setB(entry.b)
      setH(entry.h)
      setDlb(entry.dlb)
      setDlt(entry.dlt)
      setMk(entry.mk)
      setNk(entry.nk)
      setGammaf(entry.gammaf || 1.4)
      setRhoMin(entry.rhoMin)
      setDistribution(entry.distribution || 'beam')
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (name !== 'rhoMin' && name !== 'mk' && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    if (name === 'mk' && numValue < 0) {
      return 'Valor deve ser maior ou igual a 0'
    }

    if (name === 'fck' && numValue > 50) {
      return 'fck deve ser menor ou igual a 50 MPa'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.h = validateField('h', h)
    newErrors.b = validateField('b', b)
    newErrors.dlb = validateField('dlb', dlb)
    newErrors.dlt = validateField('dlt', dlt)
    newErrors.mk = validateField('mk', mk)
    newErrors.nk = validateField('nk', nk)
    newErrors.gammaf = validateField('gammaf', gammaf)
    newErrors.rhoMin = validateField('rhoMin', rhoMin)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [b, h, dlb, dlt, mk, nk, gammaf, fck, fyk, rhoMin, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  const convertToStandardUnits = () => {
    return {
      fck,
      fyk,
      h,
      dlb,
      dlt,
      md: mk * gammaf,
      nd: nk * gammaf,
      distribution,
      tShape: false,
      b,
    }
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const convertedValues = convertToStandardUnits()
      const data = {
        ...convertedValues,
        rhoMin,
      }

      setData(data)

      const response = await api.post('/concrete/bending-tension', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        b,
        h,
        dlb,
        dlt,
        mk,
        nk,
        gammaf,
        rhoMin,
        distribution,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      b,
      h,
      dlb,
      dlt,
      mk,
      nk,
      gammaf,
      fck,
      fyk,
      rhoMin,
      distribution,
      result,
      data,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Flexo-Tração.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setB(jsonData.b)
        setH(jsonData.h)
        setDlb(jsonData.dlb)
        setDlt(jsonData.dlt)
        setMk(jsonData.mk)
        setNk(jsonData.nk)
        setGammaf(jsonData.gammaf || 1.4)
        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setRhoMin(jsonData.rhoMin)
        setDistribution(jsonData.distribution || 'beam')
        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const isDoubleReinforced = Number(result.bendingResult?.asl ?? 0) > 0

  function renderRebarSection(rebars) {
    const dist = result.rebarDistribution || 'beam'

    if (dist === 'mesh') {
      if (!rebars) return <p>Nenhuma tela disponível para a área necessária</p>
      return (
        <p>
          Tela {rebars.name} — {rebars.area} cm²/m (necessário: {roundToDecimals(rebars.requiredAreaPerMeter)} cm²/m)
        </p>
      )
    }

    if (!rebars) {
      return <p>Sugestão de armadura não disponível</p>
    }

    return (
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Bitola</Th>
              <Th>Área (cm²)</Th>
              <Th>{dist === 'slab' ? 'Espaçamento (cm)' : 'Quantidade'}</Th>
            </tr>
          </thead>
          <tbody>
            {rebars.map((rebar, index) => (
              <Tr key={index}>
                <Td>{rebar.name}</Td>
                <Td>{rebar.area}</Td>
                <Td>{dist === 'slab' ? rebar.spacing : rebar.quantity}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Flexo-Tração</span>
          </div>
          <h1>📐 Flexo-Tração em Concreto Armado</h1>
          <p className="subtitle">
            Dimensionamento da armadura longitudinal de seções retangulares submetidas à flexão composta com tração
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🎯 Domínios 1, 2 e 3</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta dimensiona a armadura longitudinal de seções retangulares de concreto armado submetidas a
              flexão composta com tração, identificando automaticamente se a seção está no domínio 1 ou nos domínios
              2/3.
            </p>
            <p>
              No domínio 1 são calculadas as áreas de aço superior e inferior diretamente. Nos domínios 2/3, o cálculo
              reutiliza a lógica de flexão simples com um momento equivalente e soma a parcela adicional de aço causada
              pela força normal de tração.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Tabelas de Armadura</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância da barra inferior à face inferior (cm)</span>
              <ValidatedInput
                name="dlb"
                type="number"
                value={dlb}
                onChange={(event) => setDlb(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância da barra superior à face superior (cm)</span>
              <ValidatedInput
                name="dlt"
                type="number"
                value={dlt}
                onChange={(event) => setDlt(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Momento Característico (kN.cm)</span>
              <ValidatedInput
                name="mk"
                type="number"
                value={mk}
                onChange={(event) => setMk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Força Normal de Tração Característica (kN)</span>
              <ValidatedInput
                name="nk"
                type="number"
                value={nk}
                onChange={(event) => setNk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <InputDiv>
              <span>Taxa de aço mínima (%)</span>
              <ValidatedInput
                name="rhoMin"
                type="number"
                value={rhoMin ?? ''}
                onChange={(event) => setRhoMin(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distribuição da armadura</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={distribution}
                  onChange={(e) => setDistribution(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="beam">Viga (barras)</option>
                  <option value="slab">Laje (espaçamento)</option>
                  <option value="mesh">Tela soldada</option>
                </Select>
              </div>
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
          <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>🔗 Verificações Relacionadas</h3>
          <p style={{ marginBottom: '15px' }}>
            Quer fazer mais verificações para o mesmo elemento? Continue com o dimensionamento:
          </p>
          <ReferenceCard
            to={`/concreto/cisalhamento?b=${b}&d=${h - dlb}&fck=${fck}&fyk=${fyk}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span style={{ fontSize: '1.2rem', marginRight: '10px' }}>✂️</span>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Cisalhamento</strong>
              <small style={{ opacity: 0.8 }}>Verificação ao esforço cortante</small>
            </div>
          </ReferenceCard>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              {result.domain === 1 ? (
                <MathDiv>
                  <BlockMath math={`\\text{Domínio 1}`} />
                  <BlockMath math={`\\boxed{A_{s}'= ${result.asPrime} \\, \\text{cm}^2}`} />
                  {renderRebarSection(result.upperRebars)}
                  <BlockMath math={`\\boxed{A_{s}= ${result.as} \\, \\text{cm}^2}`} />
                  {renderRebarSection(result.lowerRebars)}
                </MathDiv>
              ) : (
                <MathDiv>
                  <BlockMath math={`\\text{Domínio } ${result.domain}`} />
                  <BlockMath math={`\\boxed{A_{s}= ${result.as} \\, \\text{cm}^2}`} />
                  {renderRebarSection(result.asRebars)}
                  {isDoubleReinforced && (
                    <>
                      <BlockMath math={`\\boxed{A_{sc}= ${result.bendingResult.asl} \\, \\text{cm}^2}`} />
                      {renderRebarSection(result.bendingResult.ascRebars)}
                    </>
                  )}
                </MathDiv>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Dados geométricos</h3>
              <MathDiv>
                <BlockMath math={`d=h-d_{lb}=${data.h}-${data.dlb}=${result.d} \\, \\text{cm}`} />
                <BlockMath math={`d'=d_{lt}=${result.dp} \\, \\text{cm}`} />
                <BlockMath math={`z=d-d'=${result.d}-${result.dp}=${result.internalLeverArm} \\, \\text{cm}`} />
              </MathDiv>
              {result.upperSteelAreaCalc !== undefined && (
                <>
                  <h3>Verificação do domínio</h3>
                  <MathDiv>
                    <BlockMath math={`A_{s,calc}'=\\dfrac{N_d \\cdot \\dfrac{z}{2} - M_d}{z \\cdot f_{yd}}`} />
                    <BlockMath
                      math={`A_{s,calc}'=\\dfrac{${data.nd} \\cdot \\dfrac{${result.internalLeverArm}}{2} - ${data.md}}{${result.internalLeverArm} \\cdot ${result.fyd}}`}
                    />
                    <BlockMath math={`A_{s,calc}'=${roundToDecimals(result.upperSteelAreaCalc)} \\, \\text{cm}^2`} />
                    {result.domain === 1 ? (
                      <BlockMath math={`A_{s,calc}' \\geq 0 \\rightarrow \\text{Domínio 1}`} />
                    ) : (
                      <BlockMath math={`A_{s,calc}' < 0 \\rightarrow \\text{Domínios 2 e 3}`} />
                    )}
                  </MathDiv>
                </>
              )}
              {result.domain === 1 ? (
                <>
                  <h3>Área de aço superior</h3>
                  <MathDiv>
                    <BlockMath math={`A_{s}'=${roundToDecimals(result.upperSteelAreaCalc)} \\, \\text{cm}^2`} />
                    <BlockMath math={`\\boxed{A_{s}'=${result.asPrime} \\, \\text{cm}^2}`} />
                    {renderRebarSection(result.upperRebars)}
                  </MathDiv>
                  <h3>Área de aço inferior</h3>
                  <MathDiv>
                    <BlockMath math={`A_{s}=\\dfrac{N_d \\cdot \\dfrac{z}{2} + M_d}{z \\cdot f_{yd}}`} />
                    <BlockMath
                      math={`A_{s}=\\dfrac{${data.nd} \\cdot \\dfrac{${result.internalLeverArm}}{2} + ${data.md}}{${result.internalLeverArm} \\cdot ${result.fyd}}`}
                    />
                    <BlockMath math={`\\boxed{A_{s}=${result.as} \\, \\text{cm}^2}`} />
                    {renderRebarSection(result.lowerRebars)}
                  </MathDiv>
                </>
              ) : (
                <>
                  <h3>Momento equivalente para flexão simples</h3>
                  <MathDiv>
                    <BlockMath math={`M_{Sd}=M_d - N_d \\cdot \\dfrac{z}{2}`} />
                    <BlockMath math={`M_{Sd}=${data.md} - ${data.nd} \\cdot \\dfrac{${result.internalLeverArm}}{2}`} />
                    <BlockMath math={`M_{Sd}=${result.msd} \\, \\text{kN}\\cdot\\text{cm}`} />
                  </MathDiv>
                  <h3>Área de aço da flexão simples</h3>
                  <MathDiv>
                    <BlockMath math={`A_{s,flexão}=${result.bendingSteelArea} \\, \\text{cm}^2`} />
                  </MathDiv>
                  <h3>Parcela adicional de aço devido a Nd</h3>
                  <MathDiv>
                    <BlockMath math={`\\Delta A_{s}=\\dfrac{N_d}{f_{yd}}`} />
                    <BlockMath math={`\\Delta A_{s}=\\dfrac{${data.nd}}{${result.fyd}}`} />
                    <BlockMath math={`\\Delta A_{s}=${roundToDecimals(result.additionalSteelArea)} \\, \\text{cm}^2`} />
                  </MathDiv>
                  <h3>Área de aço total</h3>
                  <MathDiv>
                    <BlockMath
                      math={`A_{s}=A_{s,flexão}+\\Delta A_{s}=${result.bendingSteelArea}+${roundToDecimals(result.additionalSteelArea)}`}
                    />
                    <BlockMath math={`\\boxed{A_{s}=${result.as} \\, \\text{cm}^2}`} />
                    {renderRebarSection(result.asRebars)}
                  </MathDiv>
                  {isDoubleReinforced && (
                    <>
                      <h3>Área de aço comprimida (armadura dupla)</h3>
                      <MathDiv>
                        <BlockMath math={`\\boxed{A_{sc}=${result.bendingResult.asl} \\, \\text{cm}^2}`} />
                        {renderRebarSection(result.bendingResult.ascRebars)}
                      </MathDiv>
                    </>
                  )}
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
